import { useState, useMemo, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Button,
    IconButton,
    Chip,
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Avatar,
    Card,
    CardContent,
    Tooltip,
    Collapse,
    Divider,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Context and Components
import { ROLES, useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { StatCardSkeleton, TableSkeleton, PageHeaderCard } from '../../components/common';

// Services
import { usersAPI } from '../../services/api';

// Utils and Constants
import { formatDate } from '../../utils/helper';
import { isValidEmail } from '../../utils/validation';

const roleConfig = {
    [ROLES.ADMIN]: { label: 'Admin', color: 'error' },
    [ROLES.COMPANY_ADMIN]: { label: 'Company Admin', color: 'error' },
    [ROLES.MANAGER]: { label: 'Manager', color: 'warning' },
    [ROLES.USER]: { label: 'User', color: 'default' },
};

const statusConfig = {
    active: { label: 'Active', color: 'success' },
    inactive: { label: 'Inactive', color: 'default' },
};

const mapSortFieldToApi = (field) => {
    if (field === 'createdAt') return 'created_at';
    if (field === 'status') return 'status';
    return field || 'created_at';
};

const normalizeUser = (user) => ({
    id: user.id,
    name: user.name || user.email,
    email: user.email,
    role: user.role,
    roleTitle: user.role_title || '',
    status: user.is_active ? 'active' : 'inactive',
    createdAt: user.created_at,
});

const UserManagement = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const { getActiveCompany, hasAnyRole, hasRole } = useAuth();
    const { showSuccess, showError } = useNotification();

    const [isLoading, setIsLoading] = useState(true);
    const [isStatsLoading, setIsStatsLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [totalUsersCount, setTotalUsersCount] = useState(0);
    const [stats, setStats] = useState({ totalUsers: 0, activeUsers: 0, adminCount: 0, managerCount: 0 });
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [showSearch, setShowSearch] = useState(false);
    const [sortConfig, setSortConfig] = useState({ field: 'createdAt', direction: 'desc' });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [submitError, setSubmitError] = useState('');
    const [userForm, setUserForm] = useState({
        name: '',
        email: '',
        role: ROLES.USER,
        roleTitle: '',
        status: 'active',
    });
    const [formErrors, setFormErrors] = useState({});

    const activeCompany = useMemo(() => getActiveCompany(), [getActiveCompany]);
    const activeCompanyId = activeCompany?.id || null;
    const canManageUsers = hasAnyRole([ROLES.ADMIN, ROLES.COMPANY_ADMIN]);
    const canViewUsers = canManageUsers || hasRole(ROLES.MANAGER);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
            setPage(0);
        }, 350);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    const loadUsers = useCallback(async () => {
        if (!activeCompanyId) {
            setUsers([]);
            setTotalUsersCount(0);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setSubmitError('');

        try {
            const response = await usersAPI.listByCompany(activeCompanyId, {
                page: page + 1,
                pageSize: rowsPerPage,
                search: debouncedSearchTerm || undefined,
                sortBy: mapSortFieldToApi(sortConfig.field),
                sortOrder: sortConfig.direction,
                roleFilter: roleFilter === 'all' ? undefined : roleFilter,
            });

            const items = response.items || [];
            setUsers(items.map(normalizeUser));
            setTotalUsersCount(response.total || 0);
        } catch (error) {
            setUsers([]);
            setTotalUsersCount(0);
            const message = error.message || 'Failed to load users';
            setSubmitError(message);
            showError(message);
        } finally {
            setIsLoading(false);
        }
    }, [
        activeCompanyId,
        page,
        rowsPerPage,
        debouncedSearchTerm,
        sortConfig.field,
        sortConfig.direction,
        roleFilter,
        showError,
    ]);

    useEffect(() => {
        loadUsers();
    }, [loadUsers]);

    const loadStats = useCallback(async () => {
        if (!activeCompanyId) {
            setStats({ totalUsers: 0, activeUsers: 0, adminCount: 0, managerCount: 0 });
            setIsStatsLoading(false);
            return;
        }

        setIsStatsLoading(true);
        try {
            const response = await usersAPI.getStats(activeCompanyId);
            setStats({
                totalUsers: response.total_users || 0,
                activeUsers: response.active_users || 0,
                adminCount: response.admin_count || 0,
                managerCount: response.manager_count || 0,
            });
        } catch (error) {
            const message = error.message || 'Failed to load user stats';
            showError(message);
            setStats({ totalUsers: 0, activeUsers: 0, adminCount: 0, managerCount: 0 });
        } finally {
            setIsStatsLoading(false);
        }
    }, [activeCompanyId, showError]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    const handleSort = (field) => {
        setSortConfig((prev) => ({
            field,
            direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
        setPage(0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenDialog = (user = null) => {
        if (!canManageUsers) return;

        if (user) {
            setUserForm({
                name: user.name,
                email: user.email,
                role: user.role,
                roleTitle: user.roleTitle || '',
                status: user.status,
            });
            setSelectedUser(user);
        } else {
            setUserForm({
                name: '',
                email: '',
                role: ROLES.USER,
                roleTitle: '',
                status: 'active',
            });
            setSelectedUser(null);
        }
        setSubmitError('');
        setFormErrors({});
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedUser(null);
        setSubmitError('');
        setFormErrors({});
    };

    const validateForm = () => {
        const errors = {};

        if (!userForm.name.trim()) {
            errors.name = 'Name is required';
        }

        if (!userForm.email.trim()) {
            errors.email = 'Email is required';
        } else if (!isValidEmail(userForm.email)) {
            errors.email = 'Please enter a valid email address';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleNameChange = (value) => {
        setUserForm((prev) => ({ ...prev, name: value }));

        setFormErrors((prev) => {
            if (!prev.name) return prev;
            if (value.trim()) {
                const next = { ...prev };
                delete next.name;
                return next;
            }
            return { ...prev, name: 'Name is required' };
        });
    };

    const handleEmailChange = (value) => {
        setUserForm((prev) => ({ ...prev, email: value }));

        setFormErrors((prev) => {
            if (!prev.email) return prev;

            const trimmed = value.trim();
            if (!trimmed) {
                return { ...prev, email: 'Email is required' };
            }

            if (!isValidEmail(trimmed)) {
                return { ...prev, email: 'Please enter a valid email address' };
            }

            const next = { ...prev };
            delete next.email;
            return next;
        });
    };

    const handleSaveUser = async () => {
        if (!canManageUsers) {
            showError('You do not have permission to modify users');
            return;
        }

        if (!activeCompanyId) {
            showError('No active company selected');
            return;
        }

        if (!validateForm()) return;

        setSubmitError('');

        try {
            const payload = {
                name: userForm.name.trim(),
                email: userForm.email.trim(),
                role: userForm.role,
                role_title: userForm.roleTitle.trim() || null,
                is_active: userForm.status === 'active',
            };

            if (selectedUser) {
                await usersAPI.update(selectedUser.id, activeCompanyId, payload);
                showSuccess('User updated successfully');
            } else {
                await usersAPI.create({
                    company_id: activeCompanyId,
                    ...payload,
                });
                showSuccess('User created successfully');
            }

            handleCloseDialog();
            await Promise.all([loadUsers(), loadStats()]);
        } catch (error) {
            const message = error.message || 'Failed to save user';
            setSubmitError(message);
            showError(message);
        }
    };

    const handleOpenDeleteDialog = (user) => {
        if (!canManageUsers) return;
        setSelectedUser(user);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setSelectedUser(null);
    };

    const handleDeleteUser = async () => {
        if (!canManageUsers) {
            showError('You do not have permission to modify users');
            return;
        }

        if (!selectedUser || !activeCompanyId) return;

        try {
            await usersAPI.remove(selectedUser.id, activeCompanyId);
            showSuccess('User deleted successfully');
            handleCloseDeleteDialog();
            await Promise.all([loadUsers(), loadStats()]);
        } catch (error) {
            const message = error.message || 'Failed to delete user';
            showError(message);
        }
    };

    const getInitials = (name) => {
        return name
            .split(' ')
            .filter(Boolean)
            .map((n) => n[0])
            .join('')
            .toUpperCase();
    };

    if (!canViewUsers) {
        return <Alert severity="error">You do not have permission to access user management.</Alert>;
    }

    return (
        <Box>
            <PageHeaderCard
                mb={{ xs: 2, sm: 3 }}
                title="User Management"
                subtitle="Manage users, roles, and permissions"
                titleVariant={isMobile ? 'h5' : 'h4'}
                titleFontWeight={700}
                actions={(
                    canManageUsers ? (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenDialog()}
                            size={isMobile ? 'small' : 'medium'}
                            fullWidth={isMobile}
                        >
                            Add User
                        </Button>
                    ) : null
                )}
            />

            {!canManageUsers && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    You have read-only access. Only company admins can add or edit users.
                </Alert>
            )}

            {submitError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {submitError}
                </Alert>
            )}

            {isStatsLoading ? (
                <StatCardSkeleton count={4} />
            ) : (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                    gap: { xs: 1.5, sm: 2 },
                    mb: { xs: 2, sm: 3 },
                }}>
                    {[
                        { title: 'Total Users', value: stats.totalUsers, icon: GroupIcon, color: '#1976d2' },
                        { title: 'Active Users', value: stats.activeUsers, icon: CheckCircleIcon, color: '#2e7d32' },
                        { title: 'Company Admins', value: stats.adminCount, icon: AdminPanelSettingsIcon, color: '#d32f2f' },
                        { title: 'Managers', value: stats.managerCount, icon: SupervisorAccountIcon, color: '#ed6c02' },
                    ].map((item) => {
                        const IconComponent = item.icon;
                        return (
                            <Card
                                key={item.title}
                                elevation={0}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                    borderRadius: 2,
                                    height: { xs: 100, sm: 120 },
                                    transition: 'all 0.2s ease-in-out',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                                    },
                                }}
                            >
                                <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, height: '100%', display: 'flex', alignItems: 'center' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: { xs: 1, sm: 2 }, width: '100%' }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' }, mb: 0.5, fontWeight: 500 }}>
                                                {item.title}
                                            </Typography>
                                            <Typography variant="h4" fontWeight={700} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, lineHeight: 1.2 }}>
                                                {item.value}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                p: { xs: 1, sm: 1.5 },
                                                borderRadius: 2,
                                                backgroundColor: `${item.color}12`,
                                                flexShrink: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <IconComponent sx={{ color: item.color, fontSize: { xs: 24, sm: 28 } }} />
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        );
                    })}
                </Box>
            )}

            {/* Filter */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
                    Filter
                </Typography>
                <Paper sx={{ p: 2.5, borderRadius: 2 }}>
                    <Box sx={{
                        display: 'flex',
                        gap: 2,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        flexDirection: { xs: 'column', sm: 'row' },
                    }}>
                        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={roleFilter}
                                label="Role"
                                onChange={(e) => {
                                    setRoleFilter(e.target.value);
                                    setPage(0);
                                }}
                            >
                                <MenuItem value="all">All Roles</MenuItem>
                                <MenuItem value={ROLES.COMPANY_ADMIN}>Company Admin</MenuItem>
                                <MenuItem value={ROLES.MANAGER}>Manager</MenuItem>
                                <MenuItem value={ROLES.USER}>User</MenuItem>
                            </Select>
                        </FormControl>
                        <Chip
                            label={`${totalUsersCount} results`}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    </Box>
                </Paper>
            </Box>

            {/* Users Table */}
            <Card
                elevation={0}
                sx={{
                    borderRadius: 3,
                    backgroundColor: 'background.paper',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                    border: '1px solid',
                    borderColor: 'grey.100',
                }}
            >
                <Box sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 1.5, sm: 2 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 8,
                            height: 24,
                            borderRadius: 1,
                            backgroundColor: 'primary.main',
                        }} />
                        <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                            Users List
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                            size="small"
                            onClick={() => setShowSearch(!showSearch)}
                            sx={{
                                color: showSearch ? 'primary.main' : 'text.secondary',
                                '&:hover': { backgroundColor: 'primary.50' },
                            }}
                        >
                            <SearchIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                        <Typography variant="body2" color="text.secondary">
                            {totalUsersCount} users
                        </Typography>
                    </Box>
                </Box>
                {/* Collapsible Search */}
                <Collapse in={showSearch}>
                    <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" sx={{ fontSize: 18 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                                            <CloseIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ maxWidth: 400 }}
                        />
                    </Box>
                </Collapse>

                {isLoading ? (
                    <Box sx={{ p: 2 }}>
                        <TableSkeleton columns={isMobile ? 4 : isTablet ? 5 : 6} rows={5} />
                    </Box>
                ) : (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size={isMobile ? 'small' : 'medium'}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200', minWidth: 180 }}>
                                        <TableSortLabel
                                            active={sortConfig.field === 'name'}
                                            direction={sortConfig.field === 'name' ? sortConfig.direction : 'asc'}
                                            onClick={() => handleSort('name')}
                                        >
                                            User
                                        </TableSortLabel>
                                    </TableCell>
                                    {!isTablet && (
                                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>
                                            <TableSortLabel
                                                active={sortConfig.field === 'email'}
                                                direction={sortConfig.field === 'email' ? sortConfig.direction : 'asc'}
                                                onClick={() => handleSort('email')}
                                            >
                                                Email
                                            </TableSortLabel>
                                        </TableCell>
                                    )}
                                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>
                                        <TableSortLabel
                                            active={sortConfig.field === 'role'}
                                            direction={sortConfig.field === 'role' ? sortConfig.direction : 'asc'}
                                            onClick={() => handleSort('role')}
                                        >
                                            Role
                                        </TableSortLabel>
                                    </TableCell>
                                    {!isTablet && (
                                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>
                                            Designation
                                        </TableCell>
                                    )}
                                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>
                                        <TableSortLabel
                                            active={sortConfig.field === 'status'}
                                            direction={sortConfig.field === 'status' ? sortConfig.direction : 'asc'}
                                            onClick={() => handleSort('status')}
                                        >
                                            Status
                                        </TableSortLabel>
                                    </TableCell>
                                    {!isTablet && (
                                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>
                                            <TableSortLabel
                                                active={sortConfig.field === 'createdAt'}
                                                direction={sortConfig.field === 'createdAt' ? sortConfig.direction : 'asc'}
                                                onClick={() => handleSort('createdAt')}
                                            >
                                                Created
                                            </TableSortLabel>
                                        </TableCell>
                                    )}
                                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem', borderBottom: '2px solid', borderColor: 'grey.200' }}>
                                        {canManageUsers ? 'Actions' : 'Access'}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={isMobile ? 4 : isTablet ? 5 : 6} align="center" sx={{ py: 6 }}>
                                            <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                                            <Typography color="text.secondary">No users found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    users.map((user, index) => (
                                        <TableRow
                                            key={user.id}
                                            sx={{
                                                backgroundColor: index % 2 === 0 ? 'transparent' : 'grey.50',
                                                cursor: 'pointer',
                                                transition: 'background-color 0.15s ease',
                                                '&:hover': {
                                                    backgroundColor: 'primary.50',
                                                },
                                            }}
                                        >
                                            <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar sx={{ bgcolor: 'primary.main', width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, fontSize: { xs: '0.8rem', sm: '1rem' } }}>
                                                        {getInitials(user.name)}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography fontWeight={500} sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>{user.name}</Typography>
                                                        {isTablet && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                                {user.email}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            {!isTablet && (
                                                <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                        {user.email}
                                                    </Typography>
                                                </TableCell>
                                            )}
                                            <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                                                <Chip
                                                    label={roleConfig[user.role]?.label || user.role}
                                                    color={roleConfig[user.role]?.color || 'default'}
                                                    size="small"
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: '0.7rem',
                                                        height: 24,
                                                        '& .MuiChip-label': { px: 1.5 },
                                                    }}
                                                />
                                            </TableCell>
                                            {!isTablet && (
                                                <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                        {user.roleTitle || '-'}
                                                    </Typography>
                                                </TableCell>
                                            )}
                                            <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                                                <Chip
                                                    label={statusConfig[user.status]?.label || user.status}
                                                    color={statusConfig[user.status]?.color || 'default'}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        fontWeight: 500,
                                                        fontSize: '0.7rem',
                                                        height: 24,
                                                        '& .MuiChip-label': { px: 1.5 },
                                                    }}
                                                />
                                            </TableCell>
                                            {!isTablet && (
                                                <TableCell sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                                        {formatDate(user.createdAt)}
                                                    </Typography>
                                                </TableCell>
                                            )}
                                            <TableCell align="center" sx={{ borderBottom: '1px solid', borderColor: 'grey.100' }}>
                                                {canManageUsers ? (
                                                    <>
                                                        <Tooltip title="Edit" arrow>
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleOpenDialog(user)}
                                                                    sx={{ color: 'primary.main' }}
                                                                    disabled={user.role === ROLES.COMPANY_ADMIN}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title="Delete" arrow>
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleOpenDeleteDialog(user)}
                                                                    sx={{ color: 'error.main' }}
                                                                    disabled={user.role === ROLES.COMPANY_ADMIN}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </>
                                                ) : (
                                                    <Chip label="Read Only" size="small" variant="outlined" />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <TablePagination
                    component="div"
                    count={totalUsersCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                    sx={{ borderTop: 1, borderColor: 'divider' }}
                />
            </Card>

            {/* Add/Edit User Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'secondary.contrastText' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ color: 'inherit' }} />
                        <Typography variant="h6" color="inherit">{selectedUser ? 'Edit User' : 'Add User'}</Typography>
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {submitError && <Alert severity="error">{submitError}</Alert>}
                        <TextField
                            fullWidth
                            label="Full Name"
                            value={userForm.name}
                            onChange={(e) => handleNameChange(e.target.value)}
                            error={Boolean(formErrors.name)}
                            helperText={formErrors.name}
                            required
                        />
                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={userForm.email}
                            onChange={(e) => handleEmailChange(e.target.value)}
                            error={Boolean(formErrors.email)}
                            helperText={formErrors.email}
                            required
                        />
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={userForm.role}
                                label="Role"
                                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                            >
                                <MenuItem value={ROLES.USER}>User</MenuItem>
                                <MenuItem value={ROLES.MANAGER}>Manager</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Designation / Role Title"
                            value={userForm.roleTitle}
                            onChange={(e) => setUserForm({ ...userForm, roleTitle: e.target.value })}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={userForm.status}
                                label="Status"
                                onChange={(e) => setUserForm({ ...userForm, status: e.target.value })}
                            >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} variant="outlined">Cancel</Button>
                    <Button onClick={handleSaveUser} variant="contained">
                        {selectedUser ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
                <DialogTitle sx={{ pb: 1, bgcolor: 'primary.main', color: 'secondary.contrastText' }}>
                    Delete User
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        Are you sure you want to delete <strong>{selectedUser?.name}</strong>? This action
                        cannot be undone.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} variant="outlined">Cancel</Button>
                    <Button onClick={handleDeleteUser} variant="contained" color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserManagement;
